import { t } from "@lingui/core/macro";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  type ModalProps,
  useDisclosure,
} from "@heroui/react";
import { LimeAlert, type LimeAlertProps } from "./LimeAlert";

export type UseConfirmModalProps = {
  title: string;
  content: React.ReactNode;
  errorMessages?: LimeAlertProps[];
  confirmProps: {
    label?: string;
    icon?: React.ReactNode;
    onClick: () => Promise<void>;
    isLoading?: boolean;
    isDisabled?: boolean;
    dataIdentifier?: string;
    isDestructive?: boolean;
  };
  cancelProps?: {
    label?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
  } | null;
  modalProps?: Omit<ModalProps, "children">;
  // Optional controlled mode — mirrors AlertButton's isOpen/onOpenChange
  // pass-through so the same hook backs both the wrapped trigger button
  // and any caller (e.g. a Dropdown item) that wants to drive the modal
  // imperatively.
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
};

/**
 * Headless confirm dialog. Returns an `open()` function and pre-built
 * `modal` JSX so callers can trigger the dialog from anywhere — including
 * places where AlertButton's wrapped trigger isn't usable (HeroUI
 * Dropdowns, table rows, IconButtons inside other menus, etc.).
 *
 * Behaviour matches AlertButton:
 *   - confirm button awaits `confirmProps.onClick`. If the promise rejects
 *     the modal stays open (so an inline `errorMessages` LimeAlert can
 *     surface the failure); on resolve it closes.
 *   - cancel button fires `cancelProps.onClick` then closes.
 *
 * AlertButton is now a thin wrapper around this hook — anything fixed in
 * one is fixed in the other.
 */
export function useConfirmModal({
  title,
  content,
  errorMessages,
  confirmProps,
  cancelProps,
  modalProps,
  isOpen: isOpenProp,
  onOpenChange: onOpenChangeProp,
}: UseConfirmModalProps) {
  const {
    isOpen: isInternalOpen,
    onOpen: onInternalOpen,
    onOpenChange: onInternalOpenChange,
  } = useDisclosure();

  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : isInternalOpen;
  const setOpen = isControlled
    ? (onOpenChangeProp ?? (() => {}))
    : onInternalOpenChange;

  const open = () => {
    if (isControlled) {
      setOpen(true);
      return;
    }
    onInternalOpen();
  };
  const close = () => setOpen(false);

  const modal = (
    <Modal
      placement="top-center"
      {...modalProps}
      isOpen={isOpen}
      onOpenChange={setOpen}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">{title}</ModalHeader>
            <ModalBody>
              {content}
              {errorMessages?.map((errorMessage, index) => (
                <LimeAlert key={index} {...errorMessage} />
              ))}
            </ModalBody>
            <ModalFooter>
              {cancelProps !== null && (
                <Button
                  color={confirmProps.isDestructive ? "default" : "danger"}
                  variant="flat"
                  onPress={() => {
                    if (cancelProps?.onClick) cancelProps.onClick();
                    onClose();
                  }}
                >
                  {cancelProps?.label != null ? cancelProps.label : t`Prekliči`}
                </Button>
              )}
              <Button
                color={confirmProps.isDestructive ? "danger" : "primary"}
                variant={confirmProps.isDestructive ? "flat" : "solid"}
                onPress={async () => {
                  try {
                    await confirmProps.onClick();
                    onClose();
                  } catch (err) {
                    // Keep the modal open so an inline LimeAlert can
                    // surface the failure. Logging here avoids an
                    // unhandled rejection bubbling up from HeroUI.
                    console.error("[useConfirmModal] confirm failed", err);
                  }
                }}
                isLoading={confirmProps.isLoading}
                isDisabled={confirmProps.isDisabled}
                data-identifier={confirmProps.dataIdentifier}
              >
                {confirmProps.label != null ? confirmProps.label : t`Potrdi`}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );

  return { open, close, isOpen, modal };
}
